// ════════════════════════════════════════════════════════════════════════
//  Was auf der Urkunde eines Kindes steht
//
//  Reine Rechnung, ohne React und ohne Abfrage: der Fortschritt hängt an
//  Stufenschlüsseln und weiß nichts von Lehrgängen – die Zuordnung entsteht
//  erst hier, gegen das Bundle der Fassung, die diese Klasse übt.
// ════════════════════════════════════════════════════════════════════════

import type { Bundle } from "@otto/lehrgang";
import type { StudentRow } from "../../results/lib/data.js";
import { courseProgress } from "../../results/lib/overview.js";
import type { Speed } from "../../results/lib/speed.js";

export type StudentSummary = {
  readonly stagesDone: number;
  readonly stagesTotal: number;
  readonly stars: number;
  /**
   * Wörter pro Minute vorher und nachher – dieselbe Rechnung wie in der
   * Ergebnisübersicht, deshalb aus `results/lib/speed.js` und nicht noch einmal
   * hier. Zwei Stellen, die dasselbe rechnen, rechnen es irgendwann verschieden.
   */
  readonly speed: Speed | null;
};

/**
 * Der Stand eines Kindes in EINEM Lehrgang, oder null.
 *
 * Null heißt „hier ist nichts zu berichten“ – und auf der Urkunde fällt die
 * Zeile dann weg, statt einen Strich zu zeigen. Eine leere Zeile auf einer
 * Urkunde liest sich wie ein Mangel des Kindes.
 */
export function summarize(row: StudentRow, bundle: Bundle | null): StudentSummary | null {
  if (bundle === null) return null;

  // Gezählt wird in `courseProgress` – dieselbe Rechnung, die die Übersicht und
  // der Kindblick zeigen. Stünde sie hier ein zweites Mal, könnte die Urkunde
  // eines Tages eine andere Sternzahl nennen als der Bildschirm daneben.
  const { stagesDone, stagesTotal, stars, speed } = courseProgress(row, bundle);
  if (stagesTotal === 0) return null;

  // Auch ein Kind, das nur den Test getippt hat, hat etwas zu zeigen: gemessen
  // und dann krank geworden ist kein Grund für eine leere Urkunde.
  if (stagesDone === 0 && speed === null) return null;
  return { stagesDone, stagesTotal, stars, speed };
}
